import { useState, useEffect } from 'react';
import { Save, Image as ImageIcon, Upload } from 'lucide-react';
import { useNotify } from '@/context/NotificationContext';
import { levelApi } from '@/utils/api';

export const LevelManager = () => {
    const [levels, setLevels] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [edits, setEdits] = useState<{ [key: string]: any }>({});
    const { showNotification } = useNotify();

    const fetchLevels = () => {
        setLoading(true);
        levelApi.getAll()
            .then(res => res.json())
            .then(data => Array.isArray(data) && setLevels(data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchLevels(); }, []);

    const handleLocalChange = (levelId: number, field: string, value: string) => {
        setEdits(prev => ({ ...prev, [`${levelId}-${field}`]: value }));
    };

    const handleSave = async (level: any) => {
        try {
            const updates: any = {};
            const fields = ['required_holdings', 'max_active_listings', 'platform_fee_discount', 'image_url'];
            let hasChanges = false;
            fields.forEach(f => {
                const key = `${level.level_id}-${f}`;
                if (edits[key] !== undefined) { updates[f] = edits[key]; hasChanges = true; }
            });
            if (!hasChanges) return showNotification('Info', 'No changes to save', 'info');
            const res = await levelApi.updateLevel(level.level_id, updates);
            const data = await res.json();
            if (data.success) {
                showNotification('Updated', 'Level updated successfully', 'success');
                const newEdits = { ...edits };
                fields.forEach(f => delete newEdits[`${level.level_id}-${f}`]);
                setEdits(newEdits);
                fetchLevels();
            } else {
                showNotification('Error', data.error || 'Update failed', 'error');
            }
        } catch (e) {
            console.error(e);
            showNotification('Error', 'Connection failed', 'error');
        }
    };

    const handleImageUpload = async (levelId: number, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await levelApi.upload(formData);
            const data = await res.json();
            if (data.success) {
                handleLocalChange(levelId, 'image_url', data.url);
                showNotification('Success', 'Image uploaded. Click Save to apply.', 'success');
            } else {
                showNotification('Error', data.message || 'Upload failed', 'error');
            }
        } catch (e) {
            console.error(e);
            showNotification('Error', 'Upload failed', 'error');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Manage Subscriptions</h2>
                <button onClick={fetchLevels} className="text-xs text-blue-400 font-bold uppercase hover:text-blue-300">Refresh</button>
            </div>
            {loading ? <div className="text-white/40">Loading...</div> : (
                <div className="grid gap-4">
                    {levels.map(level => {
                        const hasEdits = ['required_holdings', 'max_active_listings', 'platform_fee_discount', 'image_url'].some(
                            f => edits[`${level.level_id}-${f}`] !== undefined && edits[`${level.level_id}-${f}`] != level[f]
                        );
                        return (
                            <div key={level.level_id} className="bg-[#1c1c1e] p-4 rounded-xl border border-white/5 space-y-3">
                                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                    <h3 className="font-bold text-lg text-blue-400">{level.level_name} <span className="text-xs text-white/20">({level.level_code})</span></h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs bg-white/10 px-2 py-1 rounded">ID: {level.level_id}</span>
                                        {hasEdits && (
                                            <button onClick={() => handleSave(level)} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all">
                                                <Save size={12} /> Save
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[10px] text-white/40 uppercase block mb-1">Monthly Price (GRAM)</label>
                                        <input type="number" value={edits[`${level.level_id}-required_holdings`] ?? level.required_holdings} onChange={(e) => handleLocalChange(level.level_id, 'required_holdings', e.target.value)} className={`w-full bg-black/20 border rounded-lg px-2 py-1.5 text-sm ${edits[`${level.level_id}-required_holdings`] !== undefined ? 'border-blue-500/50 text-blue-200' : 'border-white/5 text-white'}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-white/40 uppercase block mb-1">Max Listings</label>
                                        <input type="number" value={edits[`${level.level_id}-max_active_listings`] ?? level.max_active_listings} onChange={(e) => handleLocalChange(level.level_id, 'max_active_listings', e.target.value)} className={`w-full bg-black/20 border rounded-lg px-2 py-1.5 text-sm ${edits[`${level.level_id}-max_active_listings`] !== undefined ? 'border-blue-500/50 text-blue-200' : 'border-white/5 text-white'}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-white/40 uppercase block mb-1">Fee Discount (%)</label>
                                        <input type="number" value={edits[`${level.level_id}-platform_fee_discount`] ?? level.platform_fee_discount} onChange={(e) => handleLocalChange(level.level_id, 'platform_fee_discount', e.target.value)} className={`w-full bg-black/20 border rounded-lg px-2 py-1.5 text-sm ${edits[`${level.level_id}-platform_fee_discount`] !== undefined ? 'border-blue-500/50 text-blue-200' : 'border-white/5 text-white'}`} />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-white/40 uppercase block mb-1">Background Image</label>
                                        <div className="flex items-center gap-3">
                                            <div className="w-20 h-12 rounded-lg bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                                                {(edits[`${level.level_id}-image_url`] || level.image_url) ? (
                                                    <img src={edits[`${level.level_id}-image_url`] || level.image_url} className="w-full h-full object-cover" alt="" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-white/10"><ImageIcon size={16} /></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <input type="text" placeholder="Image URL" value={edits[`${level.level_id}-image_url`] ?? (level.image_url || '')} onChange={(e) => handleLocalChange(level.level_id, 'image_url', e.target.value)} className={`w-full bg-black/20 border rounded-lg px-3 py-1.5 text-xs mb-2 ${edits[`${level.level_id}-image_url`] !== undefined ? 'border-blue-500/50 text-blue-200' : 'border-white/5 text-white'}`} />
                                                <label className="inline-flex items-center gap-2 cursor-pointer bg-white/5 hover:bg-white/10 text-white/60 hover:text-white px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors">
                                                    <Upload size={12} />
                                                    <span>Upload Image</span>
                                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleImageUpload(level.level_id, file); }} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
